import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import './SshHostKeys.css';

/** Host-key questions stack above every other dialog (Modal's default is z-50). */
export const HOST_KEY_MODAL_Z = 80;

/**
 * First connection to a server this device has never seen: show the host, the
 * key type and the SHA256 fingerprint, and let the person Accept (save + connect)
 * or Cancel. Closing the dialog is Cancel; so is Rust's 90 s timeout.
 * `conflict`: another pending prompt for this host:port shows a different
 * fingerprint — said loudly, because one of the two is not the real server.
 */
export default function SshHostKeyPromptModal({ prompt, conflict = false, onRespond }) {
  const { t } = useTranslation();
  if (!prompt) return null;
  const cancel = () => onRespond(prompt.requestId, false);
  const accept = () => onRespond(prompt.requestId, true);
  const hostLabel = `${prompt.host}:${prompt.port}`;

  return (
    <Modal
      // Above every other dialog: a host-key question can come from any
      // operation — a git sync behind the open Connect dialog used to put it
      // UNDER that dialog, unclickable, until it timed out as refused (02.10.2026).
      zIndex={HOST_KEY_MODAL_Z}
      isOpen
      onClose={cancel}
      type="warning"
      preventBackdropClose
      title={t('sshHostKeys.promptTitle', 'Unknown server')}
      subtitle={hostLabel}
    >
      <div className="space-y-4" data-testid="ssh-hostkey-prompt">
        <p className="ssh-hostkey-warning text-gray-600 dark:text-zinc-300">
          {t(
            'sshHostKeys.promptBody',
            'This is the first connection to this server from this device. Check that the fingerprint matches the one your server administrator gave you before you continue.'
          )}
        </p>
        {conflict && (
          <p
            role="alert"
            data-testid="ssh-hostkey-conflict"
            className="ssh-hostkey-warning text-red-600 dark:text-red-400"
          >
            {t(
              'sshHostKeys.promptConflict',
              'Another connection to this server is waiting with a different fingerprint. One of them is not the real server — cancel both unless you can verify this fingerprint.'
            )}
          </p>
        )}
        <dl className="ssh-hostkey-facts">
          <dt>{t('sshHostKeys.server', 'Server')}</dt>
          <dd>{hostLabel}</dd>
          <dt>{t('sshHostKeys.keyType', 'Key type')}</dt>
          <dd>{prompt.keyType}</dd>
          <dt>{t('sshHostKeys.fingerprint', 'Fingerprint')}</dt>
          <dd className="ssh-hostkey-fp" data-testid="ssh-hostkey-fingerprint">
            {prompt.fingerprint}
          </dd>
        </dl>
        <div className="modal-footer-row">
          <button
            type="button"
            onClick={cancel}
            className="ssh-hostkey-btn px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('sshHostKeys.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={accept}
            className="ssh-hostkey-btn px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
          >
            {t('sshHostKeys.accept', 'Trust and connect')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
